"use client";

import { useState, useRef } from "react";
import { RichTextInput, type RichTextInputHandle } from "./RichTextInput";
import { AttachmentTray, AddAttachmentButton } from "./AttachmentTray";
import { SubmitTooltip } from "./SubmitShortcut";
import { updatePost } from "@/lib/api/posts";
import { useAttachments } from "@/lib/hooks/useAttachments";
import type { Attachment, Post } from "@theta/api-client";

/**
 * Inline editor for a comment — the same text-plus-attachments treatment a post
 * gets from PostEditor, sized for a comment row.
 *
 * Seeded from the DOCUMENT, not from markdown: `GET /posts/{id}` hands back
 * `bodyMarkdown` and `attachments` as rows, so nothing here parses
 * `![name](upload://…)` out of a body and nothing writes it back.
 */
export function CommentEditor({
  commentId,
  initialText,
  initialAttachments,
  groupId,
  groupSlug,
  onCancel,
  onSaved,
}: {
  commentId: number;
  initialText: string;
  initialAttachments: Attachment[];
  groupId?: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  onCancel: () => void;
  onSaved: (updated: Post) => void;
}) {
  const [body, setBody] = useState(initialText);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const mentionRef = useRef<RichTextInputHandle>(null);
  const files = useAttachments(
    groupId ? { type: "group", id: groupId } : { type: "avatar" },
    initialAttachments
  );

  const canSave =
    (body.trim().length > 0 || files.hasContent) && !files.isUploading && !saving;
  const error = saveError ?? files.error;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updatePost(commentId, {
        bodyMarkdown: mentionRef.current?.getRaw() ?? body,
        attachments: files.uploadIds,
      });
      onSaved(updated);
    } catch {
      setSaveError("Kommentaren kunne ikke gemmes. Prøv igen.");
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 space-y-2">
      <RichTextInput
        ref={mentionRef}
        ariaLabel="Redigér kommentaren"
        value={body}
        onChange={setBody}
        multiline
        groupId={groupId}
        groupSlug={groupSlug}
        hideMentionAll
        autoFocus
        onPasteFiles={files.addFiles}
        onSubmit={handleSave}
        onEscape={onCancel}
        hideFormattingIcon
        className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-2 py-1.5 text-content leading-normal focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      />

      {files.attachments.length > 0 && (
        <AttachmentTray
          attachments={files.attachments}
          onRemove={files.remove}
          onMove={files.move}
          size="sm"
        />
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <SubmitTooltip label="Gem ændringer" className="relative">
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Gemmer..." : files.isUploading ? "Uploader..." : "Gem"}
          </button>
        </SubmitTooltip>
        <button
          onClick={onCancel}
          className="px-2.5 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          Annuller
        </button>
        <AddAttachmentButton
          onFiles={files.addFiles}
          label="Tilføj foto eller video"
          className="w-7 h-7 rounded-full hover:bg-green-50 dark:hover:bg-gray-700 flex items-center justify-center text-green-500 transition-colors"
        />
      </div>
    </div>
  );
}
